import type { AssistantRouteContext } from "@agroproj/backend/convex/assistant/routeContext";

const DASHBOARD_PROMPTS = [
  "Priorize meu dia",
  "Quais projetos precisam de atenção?",
  "Quais contas vencem em breve?",
  "Quais follow-ups de CRM priorizar?",
  "Quantos projetos abertos temos?",
] as const;

const PRODUCERS_LIST_PROMPTS = [
  "Busque um produtor",
  "Quais produtores têm cadastro incompleto?",
  "Priorize meu dia",
  "Quais projetos precisam de atenção?",
] as const;

const PRODUCER_DETAIL_PROMPTS = [
  "Resuma este produtor",
  "Prepare um resumo para reunião",
  "Quais projetos este produtor tem?",
  "Quais dados parecem faltar?",
] as const;

const PROJECTS_LIST_PROMPTS = [
  "Quais projetos precisam de atenção?",
  "Quantos projetos abertos temos?",
  "Busque um projeto",
  "Priorize meu dia",
] as const;

const PROJECT_DETAIL_PROMPTS = [
  "Resuma este projeto",
  "Quais pendências este projeto parece ter?",
  "Qual o status deste projeto?",
  "Quem é o produtor deste projeto?",
] as const;

const FINANCE_PROMPTS = [
  "Quais contas vencem em breve?",
  "Quais recebimentos estão atrasados?",
  "Quais pagamentos preciso priorizar?",
  "Priorize meu financeiro da semana",
] as const;

const CRM_PROMPTS = [
  "Quais follow-ups de CRM priorizar?",
  "Quais atividades estão atrasadas?",
  "Quem preciso contatar hoje?",
  "Priorize meu relacionamento com produtores",
] as const;

const GENERIC_PROMPTS = [
  "Priorize meu dia",
  "Quais projetos precisam de atenção?",
  "Quais contas vencem em breve?",
  "Quais follow-ups de CRM priorizar?",
  "Busque um produtor",
] as const;

export const MAX_VISIBLE_ASSISTANT_SUGGESTIONS = 6;

export function getAssistantSuggestions(context: AssistantRouteContext): string[] {
  let prompts: readonly string[];

  if (context.recordType === "producer") {
    prompts = PRODUCER_DETAIL_PROMPTS;
  } else if (context.recordType === "project") {
    prompts = PROJECT_DETAIL_PROMPTS;
  } else if (context.domain === "dashboard") {
    prompts = DASHBOARD_PROMPTS;
  } else if (context.domain === "producers") {
    prompts = PRODUCERS_LIST_PROMPTS;
  } else if (context.domain === "projects") {
    prompts = PROJECTS_LIST_PROMPTS;
  } else if (context.domain === "finance") {
    prompts = FINANCE_PROMPTS;
  } else if (context.domain === "crm") {
    prompts = CRM_PROMPTS;
  } else {
    prompts = GENERIC_PROMPTS;
  }

  return [...prompts].slice(0, MAX_VISIBLE_ASSISTANT_SUGGESTIONS);
}

export function getAssistantPlaceholder(context: AssistantRouteContext): string {
  if (context.recordType === "producer") {
    return "Pergunte sobre este produtor, pendências ou reunião...";
  }

  if (context.recordType === "project") {
    return "Pergunte sobre este projeto, pendências ou próximo passo...";
  }

  if (context.domain === "producers") {
    return "Pergunte sobre produtores, buscas ou prioridades...";
  }

  if (context.domain === "projects") {
    return "Pergunte sobre projetos abertos, pendências ou buscas...";
  }

  if (context.domain === "finance") {
    return "Pergunte sobre vencimentos, atrasos ou prioridades financeiras...";
  }

  if (context.domain === "crm") {
    return "Pergunte sobre follow-ups, atividades atrasadas ou contatos de hoje...";
  }

  if (context.domain === "dashboard") {
    return "Pergunte como priorizar seu dia ou o que precisa de atenção...";
  }

  return "Pergunte sobre produtores, projetos, financeiro, CRM ou contexto agro...";
}

export function getAssistantDomainDescription(context: AssistantRouteContext): string {
  if (context.recordType === "producer") {
    return "Contexto: produtor aberto na rota atual. Resumos seguros de produtores e projetos.";
  }

  if (context.recordType === "project") {
    return "Contexto: projeto aberto na rota atual. Resumos seguros de produtores e projetos.";
  }

  const domainLabels: Record<AssistantRouteContext["domain"], string> = {
    dashboard: "Contexto: painel da organização.",
    producers: "Contexto: área de produtores.",
    projects: "Contexto: área de projetos.",
    finance: "Contexto: área financeira.",
    crm: "Contexto: área de CRM.",
    settings: "Contexto: configurações.",
    unknown: "Contexto: navegação geral da organização.",
  };

  return `${domainLabels[context.domain]} Resumos seguros conforme permissões e domínios habilitados.`;
}
